import {
  Box,
  Button,
  buttonClasses,
  Card,
  CardContent,
  Link,
  Typography,
  visuallyHidden,
} from '@montugroup/design-system';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import CardMedia from '@mui/material/CardMedia';
import { styled } from '@mui/material/styles';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { ProductCartEventInfo } from '@/hooks/products/useCartEventInfo';
import { useProductDetailsPath } from '@/hooks/products/useProductDetailsPath';
import { useProductQuantity } from '@/hooks/products/useProductQuantity';
import type { PrescriptionInfo, ProductInfo } from './ProductRefillCard';
import { formatPrice } from './util';

type Image = { src: string; alt?: string };
export type IncrementProductRefillCardProps = {
  cartEventInfo?: ProductCartEventInfo;
} & Pick<PrescriptionInfo, 'maxQuantity'> &
  Pick<ProductInfo, 'id' | 'productName' | 'price' | 'supplier'> & { image?: Image };

const StyledCard = styled(Card)(({ theme }) => ({
  border: '2px solid',
  borderRadius: theme.shape.borderRadius * 2,
  borderColor: theme.palette.action.disabledBackground,
  marginBottom: theme.spacing(4),
  '&:first-of-type': {
    marginTop: theme.spacing(4),
  },
}));

const CardLayout = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  padding: theme.spacing(4),
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(3),
}));

const CardHeaderTitles = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  align-items: flex-start;
`;

const CardHeaderControls = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const InputStepperContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  borderRadius: theme.spacing(5),
  border: '1px solid',
  padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
  button: {
    padding: 0,
    margin: 0,
    minWidth: theme.spacing(4),
    fontSize: theme.typography.body1.fontSize,
  },
  // Prevent button from jumping in size when focused
  [`button.${buttonClasses.sizeMedium}.${buttonClasses.textPrimary}:not(.${buttonClasses.disabled}):not(.${buttonClasses.focusVisible}):hover`]:
    {
      padding: 0,
    },
}));

export function IncrementProductRefillCard(props: IncrementProductRefillCardProps) {
  const { id, maxQuantity, price, productName, image, supplier, cartEventInfo } = props;
  const { quantity, setQuantity } = useProductQuantity(id, cartEventInfo);
  const { path } = useProductDetailsPath();

  const increment = useCallback(
    () => setQuantity(Math.min(quantity + 1, maxQuantity)),
    [maxQuantity, quantity, setQuantity],
  );
  const decrement = useCallback(() => setQuantity(Math.max(0, quantity - 1)), [quantity, setQuantity]);

  const canDecrement = quantity > 0;
  const canIncrement = quantity < maxQuantity;

  return (
    <StyledCard elevation={0} aria-label={`Product Card for ${productName}`}>
      <CardLayout>
        <CardHeader>
          {image ? (
            <CardMedia
              component="img"
              image={image.src}
              alt={image.alt ?? ''}
              sx={(theme) => ({
                width: theme.spacing(15),
                height: theme.spacing(15),
                borderRadius: 2,
                objectFit: 'cover',
                flexShrink: 0,
                alignSelf: 'flex-start',
              })}
            />
          ) : null}
          <CardHeaderTitles>
            <Link
              aria-label="Product Details"
              variant="subtitle2"
              component={RouterLink}
              to={`${path}${id}`}
              sx={{ fontWeight: 600 }}
            >
              {productName}
            </Link>
            <Typography variant="body2">
              <span aria-label="Supplier">{supplier}</span>
              <span aria-hidden>&nbsp;|&nbsp;</span>
              <span>
                RRP <strong>{formatPrice(price)}</strong>
              </span>
            </Typography>
          </CardHeaderTitles>
          <CardHeaderControls>
            <InputStepperContainer role="group" aria-label="Quantity to Order">
              <Button onClick={decrement} disabled={!canDecrement} aria-label="Decrease by 1">
                <Remove focusable={false} />
              </Button>
              <Typography aria-hidden sx={(theme) => ({ textAlign: 'center', width: theme.spacing(5.5) })}>
                {quantity}
              </Typography>
              <span aria-live="polite" aria-label={`Current quantity: ${quantity}`} style={visuallyHidden}>
                {quantity}
              </span>
              <Button onClick={increment} disabled={!canIncrement} aria-label="Increase by 1">
                <Add focusable={false} />
              </Button>
            </InputStepperContainer>
          </CardHeaderControls>
        </CardHeader>
      </CardLayout>
    </StyledCard>
  );
}

export default IncrementProductRefillCard;
